import { type ReactNode, useEffect, useRef } from "react";
import type { PublicItem } from "../lib/publicContract.js";
import { PublicImage } from "./PublicImage.js";

/** Native modal makes the page inert and contains keyboard focus. */
export function PhotoViewer({ item, index, onChange, onClose }: {
  item: PublicItem;
  index: number;
  onChange: (index: number) => void;
  onClose: () => void;
}): ReactNode {
  const dialog = useRef<HTMLDialogElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const opener = document.activeElement;
    const element = dialog.current;
    element?.showModal();
    close.current?.focus();
    const trap = (event: KeyboardEvent): void => {
      if (event.key !== "Tab" || element === null) return;
      const buttons = Array.from(element.querySelectorAll<HTMLButtonElement>("button:not(:disabled)"));
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first?.focus();
      }
    };
    element?.addEventListener("keydown", trap);
    return () => {
      element?.removeEventListener("keydown", trap);
      element?.close();
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, []);
  const source = item.public_derivatives[index];
  return (
    <dialog ref={dialog} className="photo-viewer" aria-labelledby="photo-viewer-title"
      onCancel={(event) => { event.preventDefault(); onClose(); }}>
      <div className="photo-toolbar">
        <h2 id="photo-viewer-title" className="t-body-l">{item.title} · Photo {index + 1} of {item.public_derivatives.length}</h2>
        <button ref={close} className="btn btn--secondary" onClick={onClose} type="button">Close photo</button>
      </div>
      {source === undefined ? null : <PublicImage className="photo-enlarged" src={source}
        alt={`Approved photograph ${index + 1} of ${item.title}, SKU ${item.sku}`} />}
      <div className="photo-toolbar">
        <button className="btn btn--secondary" disabled={index === 0} type="button" onClick={() => { onChange(index - 1); }}>Previous photo</button>
        <p className="t-body-s" aria-live="polite">Photo {index + 1} of {item.public_derivatives.length}. SKU {item.sku}</p>
        <button className="btn btn--secondary" disabled={index === item.public_derivatives.length - 1} type="button" onClick={() => { onChange(index + 1); }}>Next photo</button>
      </div>
    </dialog>
  );
}
